import type { DocumentReference, Reference } from '@medplum/fhirtypes';
import {
  BILLING_DOCUMENT_KIND_EXTENSION,
  BILLING_DOCUMENT_KIND_SYSTEM,
  CLIENT_VISIBLE_TAG_CODE,
  CLIENT_VISIBLE_TAG_SYSTEM,
  type BillingDocumentKindCode,
} from '@unify/fhir-model/constants';
import { attachmentDataUrl } from '../createSecuredAttachment';

export type InsuranceCardSide = 'front' | 'back';

const SIDE_TO_KIND: Record<InsuranceCardSide, BillingDocumentKindCode> = {
  front: 'insurance-card-front',
  back: 'insurance-card-back',
};

export interface InsuranceCardDocument {
  id: string;
  side: InsuranceCardSide;
  coverageRef?: string;
  uploadedAt?: string;
  title?: string;
  contentType?: string;
  url?: string;
  status: DocumentReference['status'];
}

function cardKind(doc: DocumentReference): InsuranceCardSide | null {
  const kind = doc.extension?.find((e) => e.url === BILLING_DOCUMENT_KIND_EXTENSION)?.valueCode;
  if (kind === 'insurance-card-front') return 'front';
  if (kind === 'insurance-card-back') return 'back';
  return null;
}

export function isInsuranceCardDocument(doc: DocumentReference): boolean {
  return cardKind(doc) != null;
}

export function documentToInsuranceCard(doc: DocumentReference): InsuranceCardDocument | null {
  const side = cardKind(doc);
  if (!side) return null;
  const attachment = doc.content?.[0]?.attachment;
  const coverageRef = doc.context?.related?.find((r) =>
    r.reference?.startsWith('Coverage/'),
  )?.reference;
  return {
    id: doc.id ?? '',
    side,
    coverageRef,
    uploadedAt: doc.date ?? doc.meta?.lastUpdated,
    title: attachment?.title ?? doc.description,
    contentType: attachment?.contentType,
    url: attachment?.data && attachment.contentType
      ? attachmentDataUrl(attachment)
      : attachment?.url,
    status: doc.status,
  };
}

export function buildInsuranceCardDocumentReference(params: {
  patientRef: string;
  coverageRef?: string;
  side: InsuranceCardSide;
  fileName: string;
  contentType: string;
  supersedeId?: string;
}): DocumentReference {
  const kind = SIDE_TO_KIND[params.side];
  const related: Reference[] = [{ reference: params.patientRef }];
  if (params.coverageRef) {
    related.push({ reference: params.coverageRef });
  }
  if (params.supersedeId) {
    related.push({ reference: `DocumentReference/${params.supersedeId}` });
  }
  return {
    resourceType: 'DocumentReference',
    meta: { tag: [{ system: CLIENT_VISIBLE_TAG_SYSTEM, code: CLIENT_VISIBLE_TAG_CODE }] },
    status: 'current',
    docStatus: 'preliminary',
    type: {
      coding: [{ system: BILLING_DOCUMENT_KIND_SYSTEM, code: kind }],
      text: `Insurance card (${params.side})`,
    },
    subject: { reference: params.patientRef },
    date: new Date().toISOString(),
    description: `Insurance card ${params.side}`,
    context: { related },
    extension: [{ url: BILLING_DOCUMENT_KIND_EXTENSION, valueCode: kind }],
    content: [{
      attachment: {
        contentType: params.contentType,
        title: params.fileName,
      },
    }],
  };
}

export function currentInsuranceCards(docs: InsuranceCardDocument[]): {
  front?: InsuranceCardDocument;
  back?: InsuranceCardDocument;
} {
  const sorted = [...docs].sort(
    (a, b) => new Date(b.uploadedAt ?? 0).getTime() - new Date(a.uploadedAt ?? 0).getTime(),
  );
  return {
    front: sorted.find((d) => d.side === 'front' && d.status === 'current'),
    back: sorted.find((d) => d.side === 'back' && d.status === 'current'),
  };
}

/** Returns current front/back card photos linked to a specific Coverage. */
export function insuranceCardsForCoverage(
  cards: InsuranceCardDocument[],
  coverageId: string,
): { front?: InsuranceCardDocument; back?: InsuranceCardDocument } {
  const coverageRef = `Coverage/${coverageId}`;
  return currentInsuranceCards(cards.filter((c) => c.coverageRef === coverageRef));
}
